<template>
    <div class="messages-container">
        <div class="conversations-list">
            <div class="conversations-header">
                <h2>{{ t('messages') }}</h2>
                <button class="btn-new-conversation" @click="showNewConversationModal = true">
                    <vue-feather type="plus" size="16"></vue-feather>
                    {{ t('newMessage') }}
                </button>
            </div>
            
            <div class="chat-list">
                <div v-if="loadingChats" class="loading-chats">
                    <vue-feather type="loader" animation="spin" size="24"></vue-feather>
                    <span>{{ t('loadingConversations') }}</span>
                </div>
                <div v-else-if="recentChats.length === 0" class="no-chats">
                    <vue-feather type="message-square" size="24"></vue-feather>
                    <span>{{ t('noConversations') }}</span>
                </div>
                <div v-else class="chat-list-items">
                    <div 
                        v-for="user in recentChats" 
                        :key="user.id"
                        class="conversation-item"
                        :class="{
                            active: selectedUser?.id === user.id,
                            'has-unread': hasUnreadMessages(user)
                        }"
                        @click="selectUser(user)"
                    >
                        <div class="conversation-avatar">
                            <img 
                                class="avatar" 
                                :src="getUserAvatar(user)"
                                :alt="user.displayName || `${user.firstName} ${user.lastName}`"
                                @error="handleAvatarError"
                            />
                        </div>
                        <div class="conversation-details">
                            <div class="conversation-name">
                                {{ displayUserName(user) }}
                                <span v-if="user.isPinnedSupport" class="pinned-label">{{ t('pinned') }}</span>
                            </div>
                            <div class="conversation-preview">
                                {{ getConversationPreview(user) }}
                            </div>
                        </div>
                        <div class="conversation-meta">
                            <div v-if="user.lastMessageAt" class="conversation-time">
                                {{ formatTime(user.lastMessageAt) }}
                            </div>
                            <div v-if="hasUnreadMessages(user)" class="unread-badge">
                                {{ formatUnreadCount(getUnreadMessageCount(user)) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- New Message Modal -->
        <div v-if="showNewConversationModal" class="modal-overlay" @click="showNewConversationModal = false">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>{{ t('newMessage') }}</h3>
                    <button class="btn-close" @click="showNewConversationModal = false">
                        <vue-feather type="x" size="16"></vue-feather>
                    </button>
                </div>
                <div class="modal-body">
                    <div class="search-box">
                        <input 
                            type="text" 
                            v-model="searchQuery" 
                            :placeholder="t('searchUsers')"
                            @input="searchUsers"
                        />
                        <div v-if="searching" class="search-loading">
                            <vue-feather type="loader" animation="spin" size="16"></vue-feather>
                        </div>
                    </div>
                    <div v-if="showNewConversationModal && searchResults.length > 0" class="search-results-list">
                        <div 
                            v-for="user in searchResults" 
                            :key="user.id"
                            class="user-item"
                            @click="selectUser(user)"
                        >
                            <div class="user-avatar">
                                <img 
                                    class="avatar" 
                                    :src="getUserAvatar(user)"
                                    :alt="displayUserName(user)"
                                    @error="handleAvatarError"
                                />
                            </div>
                            <div class="user-name">
                                {{ displayUserName(user) }}
                                <vue-feather v-if="user.status === 'verified'" type="check-circle" class="verified-badge-inline" size="12" stroke-width="2"></vue-feather>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="showSearchEmptyState" class="search-empty-state">
                        <vue-feather type="search" size="18"></vue-feather>
                        <span>{{ t('noUsersFound') }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="messages-panel" v-if="selectedUser">
            <div class="messages-header">
                <router-link :to="selectedUser.username ? `/@${selectedUser.username}` : '#'" class="recipient-info">
                    <img
                        class="avatar"
                        :src="getUserAvatar(selectedUser)"
                        :alt="displayUserName(selectedUser)"
                        @error="handleAvatarError"
                    />
                    <div class="recipient-name">
                        {{ displayUserName(selectedUser) }}
                    </div>
                </router-link>
            </div>

            <div class="messages-list" ref="messagesList">
                <div v-if="loadingMessages" class="loading-messages">
                    <vue-feather type="loader" animation="spin" size="24"></vue-feather>
                    <span>{{ t('loadingMessages') }}</span>
                </div>
                <template
                    v-else
                    v-for="(message, index) in messages"
                    :key="message.id"
                >
                    <div v-if="isFirstUnreadMessage(message, index)" class="unread-divider">
                        <span>{{ t('unreadMessages') }}</span>
                    </div>
                    <div 
                        class="message"
                        :class="{
                            'message-sent': message.senderId === userId,
                            'message-unread': isUnreadThreadMessage(message)
                        }"
                    >
                        <div class="message-content" v-html="formatUserContent(message.content)">
                        </div>
                        <div class="message-time">
                            {{ formatTime(message.createdAt) }}
                        </div>
                    </div>
                </template>
            </div>

            <div class="message-input">
                <textarea 
                    ref="messageInput"
                    v-model="newMessage" 
                    :placeholder="t('typeMessage')"
                    @keydown.enter.prevent="sendMessage"
                ></textarea>
                <button 
                    class="btn-send" 
                    @click="sendMessage"
                    :disabled="!newMessage.trim() || sendingMessage"
                >
                    <vue-feather v-if="sendingMessage" type="loader" animation="spin" size="16" stroke="white"></vue-feather>
                    <vue-feather v-else type="send" size="16" stroke="white"></vue-feather>
                </button>
            </div>
        </div>

        <div v-else class="no-conversation-selected">
            <div class="empty-state">
                <vue-feather type="message-square" size="48"></vue-feather>
                <h3>{{ t('selectUser') }}</h3>
            </div>
        </div>
    </div>
</template>

<script>
import { API } from 'aws-amplify';
import { createMessageSimple, updateMessageSimple } from '../graphql/custom';
import { getUserModelByLegacyId, getUserModelLite, getUserModelLiteByUsername, listUserModelsForMessageSearch, messagesBySenderAuthOwner, messagesByReceiverAuthOwner, messagesByConversationId, listMessageModelsForAuthOwner, onCreateMessageModelSimple } from '../graphql/custom';
import { track as analyticsTrack } from '../utils/analytics';
import { searchUsersWithFallback } from '../utils/algolia.js';

export default {
    name: 'message-items',
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' }
    },
    data() {
        return {
            messages: [],
            selectedUser: null,
            newMessage: '',
            currentUser: null,
            userImages: [],
            subscription: null,
            showNewConversationModal: false,
            searchQuery: '',
            searchResults: [],
            searchTimeout: null,
            deletingMessageId: null,
            recentChats: [], // Store recent chat partners
            loadingMessages: false,
            loadingChats: false,
            searching: false,
            sendingMessage: false,
            supportUsername: 'eelamtv',
            activeUnreadMessageIds: [],
            userId: null // DynamoDB UserModel ID
        }
    },
    computed: {
        currentLanguage() {
            return this.languageRef?.value || this.languageRef || 'EN';
        },
        ta() {
            return this.currentLanguage === 'TA';
        },
        showSearchEmptyState() {
            return this.searchQuery.trim().length >= 2 && !this.searching && this.searchResults.length === 0;
        }
    },
    async created() {
        await this.initializeUser();
        this.fetchRecentChats();
        this.setupSubscription();
        
        // Check for user path parameter
        const userParam = this.$route.params.userId;
        if (userParam) {
            // Find the user in recent chats
            const user = this.recentChats.find(chat => chat.username === userParam || chat.id === userParam);
            if (user) {
                this.selectUser(user);
            } else {
                // If user not in recent chats, fetch their details
                try {
                    const userRes = await this.fetchUserForMessageRoute(userParam);
                    if (userRes) {
                        this.selectUser(userRes);
                    }
                } catch (error) {
                    console.error('Error fetching user details:', error);
                }
            }
        }
    },
    beforeUnmount() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    },
    methods: {
        t(key) {
            const translations = {
                messages: { EN: 'Messages', TA: 'செய்திகள்' },
                newMessage: { EN: 'New Message', TA: 'புதிய செய்தி' },
                loadingConversations: { EN: 'Loading conversations...', TA: 'உரையாடல்கள் ஏற்றப்படுகின்றன...' },
                noConversations: { EN: 'No conversations yet', TA: 'இன்னும் உரையாடல்கள் இல்லை' },
                pinned: { EN: 'Pinned', TA: 'மேலே வைத்தது' },
                searchUsers: { EN: 'Search users...', TA: 'பயனர்களைத் தேடுங்கள்...' },
                noUsersFound: { EN: 'No users found', TA: 'பயனர்கள் கிடைக்கவில்லை' },
                loadingMessages: { EN: 'Loading messages...', TA: 'செய்திகள் ஏற்றப்படுகின்றன...' },
                unreadMessages: { EN: 'Unread messages', TA: 'படிக்காத செய்திகள்' },
                typeMessage: { EN: 'Type a message...', TA: 'செய்தியை எழுதுங்கள்...' },
                selectUser: { EN: 'Select a user to start messaging', TA: 'செய்தி அனுப்ப பயனரைத் தேர்ந்தெடுக்கவும்' },
                supportPreview: { EN: 'How may I help you?', TA: 'உங்களுக்கு எப்படி உதவலாம்?' },
                noMessagesYet: { EN: 'No messages yet', TA: 'இன்னும் செய்திகள் இல்லை' },
                unknownUser: { EN: 'Unknown User', TA: 'அறியப்படாத பயனர்' },
                sendFailed: { EN: 'Failed to send message. Please try again.', TA: 'செய்தி அனுப்ப முடியவில்லை. மீண்டும் முயற்சிக்கவும்.' },
                yesterday: { EN: 'Yesterday', TA: 'நேற்று' }
            };
            return translations[key]?.[this.currentLanguage] || translations[key]?.EN || key;
        },
        async initializeUser() {
            const user = await API.Auth.currentAuthenticatedUser();
            this.currentUser = user.attributes;

            // Resolve DynamoDB UserModel ID
            if (this.$parent?.currentUser?.model?.id) {
                this.userId = this.$parent.currentUser.model.id;
            } else {
                const legacyUserId = this.currentUser['custom:legacyUserId'];
                if (legacyUserId) {
                    try {
                        const res = await API.graphql({
                            query: getUserModelByLegacyId,
                            variables: { legacyId: legacyUserId }
                        });
                        if (res.data.userByLegacyId?.items?.length > 0) {
                            this.userId = res.data.userByLegacyId.items[0].id;
                        }
                    } catch (e) {
                        console.error('Error resolving user ID:', e);
                    }
                }
                if (!this.userId) {
                    this.userId = this.currentUser.sub;
                }
            }
            console.log('Messages - using userId:', this.userId);
        },
        async fetchRecentChats() {
            try {
                this.loadingChats = true;
                const [sentRes, receivedRes] = await Promise.all([
                    this.fetchMessagesByAuthOwner('sender', 100),
                    this.fetchMessagesByAuthOwner('receiver', 100)
                ]);

                const allMessages = [
                    ...(sentRes || []),
                    ...(receivedRes || [])
                ];

                console.log('Fetched messages:', allMessages.length);

                // Get unique users from messages, keeping the most recent message
                const uniqueUsers = new Map();
                allMessages.forEach(message => {
                    const otherUserId = message.senderId === this.userId ?
                        message.receiverId : message.senderId;
                    const existing = uniqueUsers.get(otherUserId);
                    const unreadCount = (existing?.unreadCount || 0) +
                        (this.isConversationMessageUnreadForList(message, otherUserId) ? 1 : 0);

                    if (!existing) {
                        uniqueUsers.set(otherUserId, {
                            id: otherUserId,
                            lastMessage: message,
                            lastMessageAt: message.lastMessageAt || message.createdAt,
                            unreadCount
                        });
                        return;
                    }

                    if (new Date(message.createdAt) > new Date(existing.lastMessageAt)) {
                        uniqueUsers.set(otherUserId, {
                            ...existing,
                            lastMessage: message,
                            lastMessageAt: message.lastMessageAt || message.createdAt,
                            unreadCount
                        });
                    } else {
                        existing.unreadCount = unreadCount;
                    }
                });

                console.log('Unique users:', uniqueUsers);

                // Fetch user details for each unique user (direct key lookup)
                const usersWithDetails = (await Promise.all(
                    Array.from(uniqueUsers.values()).map(async (user) => {
                        try {
                            const userRes = await API.graphql({
                                query: getUserModelLite,
                                variables: { id: user.id }
                            });
                            const userModel = userRes.data.getUserModel;
                            if (!userModel) return null;
                            return {
                                ...userModel,
                                lastMessage: user.lastMessage,
                                lastMessageAt: user.lastMessageAt,
                                unreadCount: user.unreadCount || 0
                            };
                        } catch (e) {
                            console.error('Error fetching user:', user.id, e);
                            return null;
                        }
                    })
                )).filter(Boolean);

                console.log('Users with details:', usersWithDetails);
                // Sort by last message time
                const sortedChats = usersWithDetails.sort((a, b) =>
                    new Date(b.lastMessageAt) - new Date(a.lastMessageAt)
                );
                this.recentChats = await this.withPinnedSupportChat(sortedChats);
            } catch (error) {
                console.error('Error fetching recent chats:', error);
                this.recentChats = await this.withPinnedSupportChat([]);
            } finally {
                this.loadingChats = false;
            }
        },
        async withPinnedSupportChat(chats) {
            const supportUser = await this.fetchSupportUser();
            if (!supportUser || supportUser.id === this.userId) return chats;

            const existingIndex = chats.findIndex(user => user.id === supportUser.id);
            const existing = existingIndex >= 0 ? chats[existingIndex] : null;
            const remainingChats = existingIndex >= 0
                ? chats.filter(user => user.id !== supportUser.id)
                : chats;

            return [{
                ...supportUser,
                ...(existing || {}),
                isPinnedSupport: true,
                supportPreview: 'supportPreview'
            }, ...remainingChats];
        },
        async fetchSupportUser() {
            try {
                const res = await API.graphql({
                    query: getUserModelLiteByUsername,
                    variables: { username: this.supportUsername },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                return res.data.userByUsername?.items?.[0] || null;
            } catch (error) {
                console.error('Error fetching support user:', error);
                return null;
            }
        },
        async fetchMessages(otherUserId) {
            if (!otherUserId) return;

            try {
                this.loadingMessages = true;
                this.activeUnreadMessageIds = [];
                this.messages = await this.fetchConversationMessages(otherUserId);
                this.activeUnreadMessageIds = this.messages
                    .filter(message => this.shouldTreatMessageAsUnread(message, otherUserId))
                    .map(message => message.id);
                await this.markMessagesAsRead(otherUserId);
                this.setConversationReadAt(otherUserId, new Date().toISOString());
                this.updateRecentChatReadState(otherUserId);
            } catch (error) {
                console.error('Error fetching messages:', error);
            } finally {
                this.loadingMessages = false;
                this.scrollToBottom();
                this.focusMessageInput();
            }
        },
        async fetchConversationMessages(otherUserId) {
            const conversationId = this.getConversationId(this.userId, otherUserId);

            try {
                const res = await API.graphql({
                    query: messagesByConversationId,
                    variables: { conversationId, limit: 200 },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                const messages = res.data.messagesByConversation?.items || [];
                if (messages.length > 0) {
                    return messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                }
            } catch (error) {
                console.warn('Falling back to legacy message lookup:', error);
            }

            return this.fetchLegacyConversationMessages(otherUserId);
        },
        async fetchLegacyConversationMessages(otherUserId) {
            const [sentRes, receivedRes] = await Promise.all([
                this.fetchMessagesByAuthOwner('sender', 200),
                this.fetchMessagesByAuthOwner('receiver', 200)
            ]);

            const sentMessages = (sentRes || [])
                .filter(m => m.receiverId === otherUserId);
            const receivedMessages = (receivedRes || [])
                .filter(m => m.senderId === otherUserId);

            return [...sentMessages, ...receivedMessages].sort((a, b) =>
                new Date(a.createdAt) - new Date(b.createdAt)
            );
        },
        async fetchMessagesByAuthOwner(direction, limit = 100) {
            const authOwner = this.getCurrentUserAuthOwner();
            if (!authOwner) return [];

            try {
                const isSender = direction === 'sender';
                const res = await API.graphql({
                    query: isSender ? messagesBySenderAuthOwner : messagesByReceiverAuthOwner,
                    variables: isSender
                        ? { senderAuthOwner: authOwner, limit }
                        : { receiverAuthOwner: authOwner, limit },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
                const page = isSender ? res.data.messagesBySenderAuthOwner : res.data.messagesByReceiverAuthOwner;
                return page?.items || [];
            } catch (error) {
                console.warn(`Falling back to message list lookup for ${direction}:`, error);
                return this.fetchMessagesByAuthOwnerList(direction, limit);
            }
        },
        async fetchMessagesByAuthOwnerList(direction, limit = 100) {
            const authOwner = this.getCurrentUserAuthOwner();
            if (!authOwner) return [];

            const isSender = direction === 'sender';
            const res = await API.graphql({
                query: listMessageModelsForAuthOwner,
                variables: {
                    filter: isSender
                        ? { senderAuthOwner: { eq: authOwner } }
                        : { receiverAuthOwner: { eq: authOwner } },
                    limit
                },
                authMode: 'AMAZON_COGNITO_USER_POOLS'
            });
            return res.data.listMessageModels?.items || [];
        },
        setupSubscription() {
            this.subscription = API.graphql({
                query: onCreateMessageModelSimple,
                variables: {
                    filter: {
                        receiverId: { eq: this.userId }
                    }
                },
                authMode: 'AMAZON_COGNITO_USER_POOLS'
            }).subscribe({
                next: (event) => {
                    const newMessage = event.value.data.onCreateMessageModel;
                    if (!newMessage || newMessage.receiverId !== this.userId) return;

                    console.log('New message received:', newMessage);
                    analyticsTrack('message_received', {
                        message_id: newMessage.id,
                        sender_id: newMessage.senderId,
                        receiver_id: newMessage.receiverId,
                        active_conversation: this.selectedUser?.id === newMessage.senderId
                    });

                    // Skip messages sent by ourselves - sendMessage() already handles those
                    if (newMessage.senderId === this.userId) return;

                    // Add to current conversation if from the selected user
                    if (this.selectedUser && newMessage.senderId === this.selectedUser.id) {
                        this.messages.push(newMessage);
                        if (!this.activeUnreadMessageIds.includes(newMessage.id)) {
                            this.activeUnreadMessageIds.push(newMessage.id);
                        }
                        this.markMessagesAsRead(newMessage.senderId);
                        this.setConversationReadAt(newMessage.senderId, new Date().toISOString());
                        this.updateRecentChatReadState(newMessage.senderId);
                        this.scrollToBottom();
                    }

                    // Update chat list in-place
                    const senderId = newMessage.senderId;
                    const chatIdx = this.recentChats.findIndex(c => c.id === senderId);
                    if (chatIdx >= 0) {
                        this.recentChats[chatIdx].lastMessage = newMessage;
                        this.recentChats[chatIdx].lastMessageAt = newMessage.lastMessageAt || newMessage.createdAt;
                        this.recentChats[chatIdx].unreadCount = this.selectedUser?.id === senderId
                            ? 0
                            : (this.recentChats[chatIdx].unreadCount || 0) + 1;
                        const chat = this.recentChats.splice(chatIdx, 1)[0];
                        this.recentChats.unshift(chat);
                    } else {
                        // New conversation from unknown user - fetch their details
                        this.fetchRecentChats();
                    }
                }
            });
        },
        async sendMessage() {
            if (!this.newMessage.trim() || !this.selectedUser || this.sendingMessage) return;

            try {
                this.sendingMessage = true;
                const contentLength = this.newMessage.trim().length;
                analyticsTrack('message_send_start', {
                    receiver_id: this.selectedUser.id,
                    has_existing_thread: this.recentChats.some(chat => chat.id === this.selectedUser.id),
                    message_length_bucket: this.messageLengthBucket(contentLength)
                });
                const senderAuthOwner = this.getCurrentUserAuthOwner();
                const receiverAuthOwner = await this.getReceiverAuthOwner(this.selectedUser);
                if (!senderAuthOwner) {
                    throw this.createMessageAuthOwnerError('sender');
                }

                const messageInput = {
                    content: this.newMessage,
                    senderId: this.userId,
                    receiverId: this.selectedUser.id,
                    conversationId: this.getConversationId(this.userId, this.selectedUser.id),
                    senderAuthOwner,
                    isRead: false,
                    lastMessageAt: new Date().toISOString()
                };
                if (receiverAuthOwner) {
                    messageInput.receiverAuthOwner = receiverAuthOwner;
                }

                const res = await this.createMessageWithConversationFallback(messageInput);

                const sentMessage = res.data.createMessageModel;
                this.messages.push(sentMessage);
                analyticsTrack('message_send', {
                    message_id: sentMessage.id,
                    receiver_id: this.selectedUser.id,
                    has_existing_thread: this.recentChats.some(chat => chat.id === this.selectedUser.id),
                    message_length_bucket: this.messageLengthBucket(contentLength)
                });
                this.setConversationReadAt(this.selectedUser.id, new Date().toISOString());
                this.newMessage = '';
                this.scrollToBottom();
                this.focusMessageInput();

                // Update chat list in-place: move this user to top with latest message
                const chatIdx = this.recentChats.findIndex(c => c.id === this.selectedUser.id);
                if (chatIdx >= 0) {
                    this.recentChats[chatIdx].lastMessage = sentMessage;
                    this.recentChats[chatIdx].lastMessageAt = sentMessage.lastMessageAt || sentMessage.createdAt;
                    this.recentChats[chatIdx].unreadCount = 0;
                    // Move to top
                    const chat = this.recentChats.splice(chatIdx, 1)[0];
                    this.recentChats.unshift(chat);
                }
            } catch (error) {
                console.error('Error sending message:', error);
                analyticsTrack('message_send_failed', {
                    receiver_id: this.selectedUser?.id,
                    error_code: this.getAnalyticsErrorCode(error),
                    missing_auth_owner_role: error?.missingAuthOwnerRole || undefined
                });
                this.$toast.error(this.t('sendFailed'));
            } finally {
                this.sendingMessage = false;
            }
        },
        async createMessageWithConversationFallback(messageInput) {
            try {
                return await API.graphql({
                    query: createMessageSimple,
                    variables: { input: messageInput },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
            } catch (error) {
                if (!this.isConversationIdSchemaError(error)) {
                    throw error;
                }

                const legacyInput = { ...messageInput };
                delete legacyInput.conversationId;
                delete legacyInput.senderAuthOwner;
                delete legacyInput.receiverAuthOwner;
                console.warn('Backend does not support conversationId yet; retrying message send without it.');
                return API.graphql({
                    query: createMessageSimple,
                    variables: { input: legacyInput },
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                });
            }
        },
        isConversationIdSchemaError(error) {
            const message = JSON.stringify(error?.errors || error || '').toLowerCase();
            return (message.includes('conversationid') || message.includes('senderauthowner') || message.includes('receiverauthowner')) &&
                (message.includes('unknown') || message.includes('validation') || message.includes('field'));
        },
        createMessageAuthOwnerError(role) {
            const error = new Error(`Missing message ${role} auth owner`);
            error.code = 'missing_message_auth_owner';
            error.missingAuthOwnerRole = role;
            return error;
        },
        selectUser(user) {
            this.selectedUser = user;
            analyticsTrack('message_thread_open', {
                other_user_id: user.id,
                has_unread: this.hasUnreadMessages(user),
                unread_count: this.getUnreadMessageCount(user),
                is_pinned_support: !!user.isPinnedSupport
            });
            this.fetchMessages(user.id);
            this.showNewConversationModal = false;
            this.searchQuery = '';
            this.searchResults = [];

            // Update URL without triggering Vue Router lifecycle
            window.history.replaceState({}, '', user.username ? `/messages/${user.username}` : '/messages');
        },
        focusMessageInput() {
            this.$nextTick(() => {
                this.$refs.messageInput?.focus();
            });
        },
        async fetchUserForMessageRoute(userParam) {
            const usernameRes = await API.graphql({
                query: getUserModelLiteByUsername,
                variables: { username: userParam },
                authMode: 'AMAZON_COGNITO_USER_POOLS'
            });
            const byUsername = usernameRes.data.userByUsername?.items?.[0];
            if (byUsername) return byUsername;

            const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userParam);
            if (!uuidLike) return null;

            const userRes = await API.graphql({
                query: getUserModelLite,
                variables: { id: userParam },
                authMode: 'AMAZON_COGNITO_USER_POOLS'
            });
            return userRes.data.getUserModel || null;
        },
        hasUnreadMessages(user) {
            return this.getUnreadMessageCount(user) > 0;
        },
        getUnreadMessageCount(user) {
            if (!user?.lastMessage || user.lastMessage.senderId === this.userId) return 0;
            if (Number.isFinite(user.unreadCount) && user.unreadCount > 0) {
                return user.unreadCount;
            }

            const lastReadAt = this.getConversationReadAt(user.id);
            if (lastReadAt) {
                return new Date(user.lastMessage.createdAt) > new Date(lastReadAt) ? 1 : 0;
            }

            return user.lastMessage.isRead === false ? 1 : 0;
        },
        formatUnreadCount(count) {
            return count > 9 ? '9+' : count;
        },
        messageLengthBucket(length) {
            if (length <= 0) return 'empty';
            if (length <= 50) return '1_50';
            if (length <= 200) return '51_200';
            if (length <= 1000) return '201_1000';
            return '1000_plus';
        },
        getAnalyticsErrorCode(error) {
            if (error?.code) return error.code;
            const message = String(error?.errors?.[0]?.message || error?.message || error?.name || '').toLowerCase();
            if (!message) return 'unknown';
            if (message.includes('missing message') && message.includes('auth owner')) return 'missing_message_auth_owner';
            if (message.includes('not authorized') || message.includes('unauthorized')) return 'unauthorized';
            if (message.includes('network') || message.includes('fetch')) return 'network_error';
            if (message.includes('timeout') || message.includes('timed out')) return 'timeout';
            if (message.includes('validation')) return 'validation_error';
            return 'error';
        },
        isConversationMessageUnreadForList(message, otherUserId) {
            if (!message || message.senderId !== otherUserId || message.receiverId !== this.userId) {
                return false;
            }

            const lastReadAt = this.getConversationReadAt(otherUserId);
            if (lastReadAt) {
                return new Date(message.createdAt) > new Date(lastReadAt);
            }

            return message.isRead === false;
        },
        shouldTreatMessageAsUnread(message, otherUserId) {
            if (!message || message.senderId !== otherUserId || message.receiverId !== this.userId) {
                return false;
            }

            const lastReadAt = this.getConversationReadAt(otherUserId);
            if (lastReadAt) {
                return new Date(message.createdAt) > new Date(lastReadAt);
            }

            return message.isRead === false;
        },
        isUnreadThreadMessage(message) {
            return this.activeUnreadMessageIds.includes(message.id);
        },
        isFirstUnreadMessage(message, index) {
            return this.isUnreadThreadMessage(message) &&
                !this.messages.slice(0, index).some(previous => this.isUnreadThreadMessage(previous));
        },
        messageReadStorageKey(otherUserId) {
            return `messagesLastRead:${this.userId}:${otherUserId}`;
        },
        getConversationId(userIdA, userIdB) {
            return [userIdA, userIdB].filter(Boolean).sort().join('#');
        },
        getCurrentUserAuthOwner() {
            return this.currentUser?.sub || this.$parent?.currentUser?.sub || this.$parent?.currentUser?.model?.authOwner || '';
        },
        async getReceiverAuthOwner(user) {
            if (user?.authOwner) return user.authOwner;
            if (!user?.id) return '';

            const res = await API.graphql({
                query: getUserModelLite,
                variables: { id: user.id },
                authMode: 'AMAZON_COGNITO_USER_POOLS'
            });
            const userModel = res.data.getUserModel;
            if (userModel?.authOwner) {
                this.selectedUser = {
                    ...this.selectedUser,
                    authOwner: userModel.authOwner
                };
                return userModel.authOwner;
            }

            return '';
        },
        getConversationReadAt(otherUserId) {
            if (!this.userId || !otherUserId) return '';
            return localStorage.getItem(this.messageReadStorageKey(otherUserId)) || '';
        },
        setConversationReadAt(otherUserId, timestamp) {
            if (!this.userId || !otherUserId || !timestamp) return;
            localStorage.setItem(this.messageReadStorageKey(otherUserId), timestamp);
        },
        updateRecentChatReadState(otherUserId) {
            const chat = this.recentChats.find(user => user.id === otherUserId);
            if (chat?.lastMessage?.receiverId === this.userId) {
                chat.lastMessage.isRead = true;
                chat.unreadCount = 0;
            }
            window.dispatchEvent(new CustomEvent('eelam:messages-read'));
        },
        getConversationPreview(user) {
            const preview = this.stripBBCode(user.lastMessage?.content);
            if (preview) return preview;
            if (user.supportPreview === 'supportPreview' || user.isPinnedSupport) return this.t('supportPreview');
            return this.t('noMessagesYet');
        },
        getParticipantName(conversation) {
            if (!conversation || !conversation.participant) return this.t('unknownUser');
            const otherParticipant = conversation.participant;
            return otherParticipant ? (otherParticipant.displayName || `${otherParticipant.firstName} ${otherParticipant.lastName}`) : this.t('unknownUser');
        },
        getParticipantAvatar(conversation) {
            if (!conversation || !conversation.participant) return '';
            const otherParticipant = conversation.participant;
            if (!otherParticipant) return '';
            
            const userImage = this.userImages.find(img => img.userId === otherParticipant.id);
            return userImage ? userImage.signedURL : '';
        },
        getOtherParticipantId(conversation) {
            return conversation.participant?.id;
        },
        formatTime(timestamp) {
            if (!timestamp) return '';
            const date = new Date(timestamp);
            const now = new Date();
            const locale = this.ta ? 'ta-IN' : undefined;
            const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

            // Same day - just show time
            if (date.toDateString() === now.toDateString()) {
                return time;
            }

            // Yesterday
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            if (date.toDateString() === yesterday.toDateString()) {
                return `${this.t('yesterday')} ${time}`;
            }

            // Within last 7 days - show day name
            const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
            if (diffDays < 7) {
                const dayName = date.toLocaleDateString(locale, { weekday: 'short' });
                return dayName + ' ' + time;
            }

            // Same year - show month and day
            if (date.getFullYear() === now.getFullYear()) {
                return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' }) + ' ' + time;
            }

            // Different year - show full date
            return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' }) + ' ' + time;
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const messagesList = this.$refs.messagesList;
                if (messagesList) {
                    messagesList.scrollTop = messagesList.scrollHeight;
                }
            });
        },
        async searchUsers() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }

            const term = this.searchQuery.trim();
            if (!term || term.length < 2) {
                this.searchResults = [];
                this.searching = false;
                return;
            }

            this.searching = true;
            this.searchTimeout = setTimeout(async () => {
                try {
                    const searchTerm = this.searchQuery.trim();

                    // Try Algolia first (same index the header search uses).
                    let results = await this.searchUsersWithAlgolia(searchTerm);

                    // Fall back to GraphQL if Algolia is unavailable or returns nothing.
                    if (results === null || results.length === 0) {
                        const graphqlResults = await this.searchUsersWithGraphql(searchTerm);
                        if (results === null || graphqlResults.length > 0) {
                            results = graphqlResults;
                        }
                    }

                    if (this.searchQuery.trim() !== searchTerm) return;
                    this.searchResults = results.slice(0, 20);
                } catch (error) {
                    console.error('Error searching users:', this.searchQuery, error);
                    this.searchResults = [];
                } finally {
                    this.searching = false;
                }
            }, 300);
        },
        async searchUsersWithAlgolia(searchTerm) {
            const term = searchTerm.replace(/^@/, '').trim();
            if (!term) return [];
            const response = await searchUsersWithFallback(term, { hitsPerPage: 20 });
            if (response === null) return null; // Algolia not configured / errored → trigger fallback
            return (response.results || [])
                .filter(hit => hit?.id && hit.id !== this.userId && hit.status !== 'banned')
                .map(hit => ({
                    id: hit.id,
                    username: hit.username,
                    displayName: hit.displayName,
                    firstName: hit.firstName,
                    lastName: hit.lastName,
                    status: hit.status
                }));
        },
        async searchUsersWithGraphql(searchTerm) {
            const resultsById = new Map();

            const addUsers = (users = []) => {
                users
                    .filter(user => user?.id && user.id !== this.userId)
                    .filter(user => this.userMatchesSearch(user, searchTerm))
                    .forEach(user => resultsById.set(user.id, user));
            };

            await Promise.all(
                this.usernameSearchVariants(searchTerm).map(async username => {
                    try {
                        const res = await API.graphql({
                            query: getUserModelLiteByUsername,
                            variables: { username },
                            authMode: 'AMAZON_COGNITO_USER_POOLS'
                        });
                        addUsers(res.data.userByUsername?.items || []);
                    } catch (error) {
                        console.warn('Username search failed:', username, error);
                    }
                })
            );

            try {
                let nextToken = null;
                for (let page = 0; page < 8; page += 1) {
                    const usersRes = await API.graphql({
                        query: listUserModelsForMessageSearch,
                        variables: {
                            limit: 200,
                            nextToken
                        },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    });
                    const pageData = usersRes.data.listUserModels;
                    addUsers(pageData?.items || []);
                    nextToken = pageData?.nextToken || null;
                    if (!nextToken || resultsById.size >= 20) break;
                }
            } catch (error) {
                console.warn('User list search fallback failed:', error);
            }

            return Array.from(resultsById.values());
        },
        usernameSearchVariants(term) {
            const compact = term.replace(/^@/, '').trim();
            const lower = compact.toLowerCase();
            const title = lower.charAt(0).toUpperCase() + lower.slice(1);
            return [...new Set([compact, lower, title])].filter(Boolean);
        },
        userMatchesSearch(user, term) {
            const needle = term.replace(/^@/, '').trim().toLowerCase();
            if (!needle) return false;
            return [
                user.username,
                user.displayName,
                user.firstName,
                user.lastName,
                `${user.firstName || ''} ${user.lastName || ''}`.trim()
            ].some(value => String(value || '').toLowerCase().includes(needle));
        },
        displayUserName(user) {
            if (!user) return this.t('unknownUser');
            return user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || this.t('unknownUser');
        },
        getUserAvatar(user) {
            if (!user || !user.id) return '/avatar-placeholder.svg';
            if (user.legacyAvatar) {
                if (user.legacyAvatar.startsWith('public/')) {
                    return `https://cdn1.eelam.tv/${user.legacyAvatar}`;
                }
                return `https://cdn.eelam.tv/${user.legacyAvatar}`;
            }
            return `https://cdn1.eelam.tv/public/images/user/${user.id}/avatar.png`;
        },
        handleAvatarError(event) {
            if (event.target.src.endsWith('/avatar-placeholder.svg')) return;
            event.target.src = '/avatar-placeholder.svg';
        },
        async markMessagesAsRead(otherUserId = null) {
            const unreadMessages = this.messages.filter(
                m => !m.isRead &&
                    m.receiverId === this.userId &&
                    (!otherUserId || m.senderId === otherUserId)
            );

            for (const message of unreadMessages) {
                try {
                    await API.graphql({
                        query: updateMessageSimple,
                        variables: {
                            input: {
                                id: message.id,
                                isRead: true
                            }
                        },
                        authMode: 'AMAZON_COGNITO_USER_POOLS'
                    });
                    message.isRead = true;
                } catch (error) {
                    console.error('Error marking message as read:', error);
                }
            }
        }
    }
}
</script>

<style scoped>
.messages-container {
    display: flex;
    height: calc(100vh - 60px);
    background: var(--bg-card);
}

.conversations-list {
    width: 350px;
    border-right: 1px solid var(--border-light);
    display: flex;
    flex-direction: column;
    background: var(--bg-card);
}

.conversations-header {
    padding: 20px;
    border-bottom: 1px solid var(--border-light);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.conversations-header h2 {
    color: var(--text-secondary);
    margin: 0;
}

.btn-new-conversation {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: var(--accent-primary);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
}

.btn-new-conversation:hover {
    background: var(--accent-hover);
}

.chat-list {
    flex: 1;
    overflow-y: auto;
}

.chat-list-header {
    padding: 20px;
    border-bottom: 1px solid var(--border-light);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.new-chat-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-secondary);
}

.chat-list-items {
    flex: 1;
    overflow-y: auto;
}

.conversation-item {
    display: flex;
    padding: 15px;
    border-bottom: 1px solid var(--border-light);
    cursor: pointer;
    transition: background-color 0.2s;
}

.conversation-item:hover {
    background-color: var(--bg-hover);
}

.conversation-item.active {
    background-color: var(--bg-tertiary);
    border-left: 3px solid var(--accent-primary);
}

.conversation-item.has-unread:not(.active) {
    background-color: color-mix(in srgb, var(--accent-primary) 7%, var(--bg-card));
}

.conversation-avatar {
    margin-right: 12px;
}

.avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    background-color: var(--bg-tertiary);
}

.conversation-details {
    flex: 1;
    min-width: 0;
}

.conversation-name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    margin-bottom: 4px;
    color: var(--text-secondary);
}

.conversation-item.has-unread .conversation-name {
    font-weight: 700;
}

.pinned-label {
    font-size: 11px;
    line-height: 1;
    font-weight: 600;
    color: var(--text-tertiary);
    border: 1px solid var(--border-light);
    border-radius: 999px;
    padding: 3px 6px;
}

.conversation-preview {
    font-size: 14px;
    color: var(--text-tertiary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.conversation-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
}

.conversation-time {
    font-size: 12px;
    color: var(--text-muted);
    margin-bottom: 4px;
}

.unread-badge {
    min-width: 14px;
    height: 14px;
    padding: 0 3px;
    border-radius: 999px;
    background-color: #ff3131;
    border: 2px solid var(--bg-card);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
    text-align: center;
    box-sizing: content-box;
}

.messages-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: var(--bg-secondary);
}

.messages-header {
    padding: 20px;
    border-bottom: 1px solid var(--border-light);
    background: var(--bg-card);
}

.recipient-info {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
}

.recipient-info:hover .recipient-name {
    color: var(--accent-primary);
}

.recipient-info .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
}

.recipient-name {
    font-weight: 500;
    font-size: 18px;
    color: var(--text-secondary);
}

.messages-list {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.message {
    max-width: 70%;
    padding: 12px 16px;
    border-radius: 12px;
    background-color: var(--bg-card);
    color: var(--text-secondary);
    box-shadow: 0 1px 2px var(--shadow-color);
}

.message-unread:not(.message-sent) {
    border: 1px solid color-mix(in srgb, var(--accent-primary) 32%, transparent);
}

.unread-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    color: var(--accent-primary);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0;
    margin: 4px 0;
}

.unread-divider::before,
.unread-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: color-mix(in srgb, var(--accent-primary) 35%, var(--border-light));
}

.unread-divider span {
    white-space: nowrap;
}

.message-content {
    line-height: 1.4;
    word-break: break-word;
}

.message-content :deep(.user-content-link) {
    color: #4fc3f7;
    text-decoration: underline;
    word-break: break-all;
}

.message-sent .message-content :deep(.user-content-link) {
    color: #fff;
    text-decoration: underline;
}

.message-sent {
    align-self: flex-end;
    background-color: var(--accent-primary);
    color: white;
}

.message-sent .message-time {
    color: rgba(255, 255, 255, 0.8);
}

.message-time {
    font-size: 12px;
    color: var(--text-muted);
    margin-top: 4px;
}

.message-input {
    padding: 20px;
    border-top: 1px solid var(--border-light);
    display: flex;
    gap: 12px;
    background: var(--bg-card);
}

.message-input textarea {
    flex: 1;
    padding: 12px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    resize: none;
    background: var(--bg-input);
    color: var(--text-secondary);
    font-family: inherit;
    font-size: 14px;
}

.message-input textarea::placeholder {
    color: var(--text-muted);
}

.message-input textarea:focus {
    outline: none;
    border-color: var(--accent-primary);
}

.btn-send {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: var(--accent-primary);
    color: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s;
}

.btn-send:hover:not(:disabled) {
    background-color: var(--accent-hover);
}

.btn-send:disabled {
    background-color: var(--bg-tertiary);
    cursor: not-allowed;
}

.no-conversation-selected {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-secondary);
}

.empty-state {
    text-align: center;
    color: var(--text-tertiary);
}

.empty-state h3 {
    margin-top: 16px;
    font-weight: 500;
    color: var(--text-secondary);
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: var(--bg-card);
    border-radius: 12px;
    width: 400px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 20px var(--shadow-dropdown);
}

.modal-header {
    padding: 16px;
    border-bottom: 1px solid var(--border-light);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-header h3 {
    margin: 0;
    color: var(--text-secondary);
}

.modal-body {
    padding: 16px;
    overflow-y: auto;
}

.search-box {
    position: relative;
    margin-bottom: 16px;
}

.search-box input {
    width: 100%;
    height: 42px;
    padding: 10px 12px;
    padding-right: 42px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    font-size: 14px;
    line-height: 20px;
    background: var(--bg-input);
    color: var(--text-secondary);
    box-sizing: border-box;
}

.search-box input::placeholder {
    color: var(--text-muted);
}

.search-box input:focus {
    outline: none;
    border-color: var(--accent-primary);
}

.search-loading {
    position: absolute;
    top: 16px;
    right: 5px;
    bottom: 0;
    width: 40px;
    color: var(--text-tertiary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.search-loading svg {
    display: block;
}

.search-results-list {
    border-bottom: 1px solid var(--border-light);
}

.search-empty-state {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 4px 4px;
    color: var(--text-tertiary);
    font-size: 14px;
}

.user-item {
    display: flex;
    align-items: center;
    padding: 12px;
    cursor: pointer;
    transition: background-color 0.2s;
    border-radius: 8px;
}

.user-item:hover {
    background-color: var(--bg-hover);
}

.user-avatar {
    margin-right: 12px;
    width: 40px;
    height: 40px;
}

.user-avatar .avatar {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
}

.user-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-secondary);
}

.verified-badge-inline {
    color: #ff1314;
    margin-left: 3px;
    vertical-align: middle;
}

.btn-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-tertiary);
    border-radius: 4px;
    transition: background-color 0.2s;
}

.btn-close:hover {
    background-color: var(--bg-hover);
}

.btn-delete {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: var(--text-tertiary);
    transition: color 0.2s;
}

.btn-delete:hover:not(:disabled) {
    color: var(--accent-danger);
}

.btn-delete:disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.spinning {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.loading-messages {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: var(--text-tertiary);
    gap: 12px;
}

.loading-messages span {
    font-size: 14px;
}

.loading-chats {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: var(--text-tertiary);
    gap: 12px;
}

.loading-chats span {
    font-size: 14px;
}

.no-chats {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: var(--text-tertiary);
}

.no-chats span {
    margin-top: 16px;
    font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
    .messages-container {
        flex-direction: column;
    }

    .conversations-list {
        width: 100%;
        max-height: 40vh;
    }

    .messages-panel {
        height: 60vh;
    }
}
</style>
